/**
 * Widget Configuration Utility
 * Dashboard widget'larının yapılandırmasını yönetir
 */

export interface WidgetConfig {
  id: string;
  title: string;
  visible: boolean;
  order: number;
  type: 'statistic' | 'chart' | 'list' | 'action';
  size: 'small' | 'medium' | 'large';
}

export const DEFAULT_WIDGETS: WidgetConfig[] = [
  { id: 'totalStations', title: 'Toplam İstasyon', visible: true, order: 1, type: 'statistic', size: 'small' },
  { id: 'totalCampaigns', title: 'Toplam Kampanya', visible: true, order: 2, type: 'statistic', size: 'small' },
  { id: 'monthlyRevenue', title: 'Aylık Gelir', visible: true, order: 3, type: 'statistic', size: 'small' },
  { id: 'averageRating', title: 'Ortalama Değerlendirme', visible: true, order: 4, type: 'statistic', size: 'small' },
  { id: 'revenueChart', title: 'Gelir Trendi', visible: true, order: 5, type: 'chart', size: 'large' },
  { id: 'campaignPie', title: 'Kampanya Durumu', visible: true, order: 6, type: 'chart', size: 'medium' },
  { id: 'topStations', title: 'En İyi Performans', visible: true, order: 7, type: 'list', size: 'medium' },
  { id: 'quickActions', title: 'Hızlı İşlemler', visible: true, order: 8, type: 'action', size: 'medium' },
  { id: 'yearlyRevenue', title: 'Yıllık Gelir', visible: true, order: 9, type: 'statistic', size: 'small' },
  { id: 'totalVisits', title: 'Toplam Ziyaret', visible: true, order: 10, type: 'statistic', size: 'small' },
  { id: 'totalRatings', title: 'Toplam Değerlendirme', visible: true, order: 11, type: 'statistic', size: 'small' },
  { id: 'verifiedStations', title: 'Doğrulanmış İstasyon', visible: true, order: 12, type: 'statistic', size: 'small' }
];

const STORAGE_KEY = 'dashboard_widget_config';

/**
 * Widget konfigürasyonunu localStorage'dan yükle
 */
export const loadWidgetConfig = (): WidgetConfig[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Eksik widget'ları default'lardan ekle
      const storedIds = parsed.map((w: WidgetConfig) => w.id);
      const missingWidgets = DEFAULT_WIDGETS.filter(w => !storedIds.includes(w.id));
      return [...parsed, ...missingWidgets].sort((a, b) => a.order - b.order);
    }
  } catch (error) {
    console.error('Widget config load error:', error);
  }
  return DEFAULT_WIDGETS;
};

/**
 * Widget konfigürasyonunu localStorage'a kaydet
 */
export const saveWidgetConfig = (config: WidgetConfig[]): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  } catch (error) {
    console.error('Widget config save error:', error);
  }
};

/**
 * Widget'ı göster/gizle
 */
export const toggleWidgetVisibility = (widgetId: string, config: WidgetConfig[]): WidgetConfig[] => {
  return config.map(widget =>
    widget.id === widgetId ? { ...widget, visible: !widget.visible } : widget
  );
};

/**
 * Widget sırasını güncelle
 */
export const updateWidgetOrder = (widgetId: string, newOrder: number, config: WidgetConfig[]): WidgetConfig[] => {
  const updated = config.map(widget => {
    if (widget.id === widgetId) {
      return { ...widget, order: newOrder };
    }
    return widget;
  });
  return updated.sort((a, b) => a.order - b.order);
};

/**
 * Widget'ları sırala (yukarı/aşağı taşı)
 */
export const moveWidget = (widgetId: string, direction: 'up' | 'down', config: WidgetConfig[]): WidgetConfig[] => {
  const currentIndex = config.findIndex(w => w.id === widgetId);
  if (currentIndex === -1) return config;

  const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
  if (newIndex < 0 || newIndex >= config.length) return config;

  const newConfig = [...config];
  [newConfig[currentIndex], newConfig[newIndex]] = [newConfig[newIndex], newConfig[currentIndex]];
  
  // Order değerlerini güncelle
  return newConfig.map((widget, index) => ({ ...widget, order: index + 1 }));
};

/**
 * Widget konfigürasyonunu sıfırla
 */
export const resetWidgetConfig = (): WidgetConfig[] => {
  localStorage.removeItem(STORAGE_KEY);
  return DEFAULT_WIDGETS;
};

